import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type {
	APIData,
	CreateRecordProperties,
	DateRangeInterface,
	DSData,
	DSSetup,
	PopupData,
	RecordData,
	RequestError,
	Service,
} from 'webapp-types';
import type { GoalTargetValueType } from 'webapp/enums/enums';
import {
	BillingFeature,
	FakeModelIdPrefix,
	GoalFormatType,
	GoalOwnerScheduleType,
	GoalProgressType,
	GoalStatus,
	GoalTargetSuccessCondition,
	GoalType,
	ModelName,
	PopupEvent,
	ScheduleType,
	TimeframeType,
	TimeUnit,
	Unit,
	UserAccess,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { equal, sortBy, uniq, uniqueId } from 'webapp/utils/common';
import type { ValueRowsByTimeUnit } from 'webapp/utils/goal-value-rows';
import {
	createMissingRowsFromLayoutCache,
	createRowsFromLayoutCache,
	findRowWithTimestamp,
	getGoalRangeTimeUnitForPrimaryGranulation,
	TargetEndDateFormat,
} from 'webapp/utils/goal-value-rows';
import { sanitize } from 'webapp/utils/html';

import CommonPopupAccessPermissionsComponent from 'webapp/components/common/popup-access-permissions';
import GoalPopupAdvancedSetupComponent from 'webapp/components/goal/popup-advanced-setup';
import GoalPopupCreateComponent from 'webapp/components/goal/popup-create';
import GoalPopupEditComponent from 'webapp/components/goal/popup-edit';
import GoalPopupSelectorComponent from 'webapp/components/goal/popup-selector';
import GoalPopupUpdateProgressComponent from 'webapp/components/goal/popup-update-progress';
import GoalsInsightsDrawerPopupComponent from 'webapp/components/goals/insights/drawer-popup';
import { TopStrategyItemId as TreeTopStrategyItemId } from 'webapp/components/goals/tree/index';
import HomePopupPickGoalComponent from 'webapp/components/home/popup-pick-goal';

import type { GoalLog } from 'webapp/models-custom';
import DateRange from 'webapp/models-custom/date-range';
import Goal from 'webapp/models-custom/goal';
import type GoalValueRow from 'webapp/models-custom/goal/value-row';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type GoalRecord from 'webapp/models/goal';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { RSjqXHR, RSPromise } from 'webapp/services/request';

type RequestVariablePrefix = 'spaceGoals' | 'spaceGoalsData' | 'clientGoals' | 'clientGoalsData';

export type GoalRangeTimeUnit = Exclude<TimeUnit, TimeUnit.HOUR>;
export type TargetTypeAndGranulationOnChange = (
	targetValueType: GoalTargetValueType,
	primaryGranulation: GoalRangeTimeUnit,
	otherMetric?: DSSetup.MetricSetupData,
) => void;

export interface TreeItemData {
	id: number;
	goal: Goal;
	parentId: number;
	depth: number; // used in table view, not tree
}

export default class ModelManagerGoalsService extends ModelManagerForStoreService<
	GoalRecord,
	Goal
> {
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('socket') declare socketService: Service.Socket;
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('timeframe') declare timeframeService: Service.Timeframe;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('constants') declare constantsService: Service.Constants;

	private _goalModels: Goal[] = [];

	@cached
	get goalModels(): Goal[] {
		let filteredRecords = this.savedRecords;
		if (!this.isGoalsOKRsEnabled) {
			// do not show any objectives or progress-subgoals type if we dont have the feature
			filteredRecords = filteredRecords.filter((item) => {
				return item.allowedWithoutOKRsFeature;
			});
		}
		this.updateModelCache(this._goalModels, filteredRecords);
		return this._goalModels;
	}

	@cached
	get goalForSpaceModels(): Goal[] {
		return this.goalModels.filter((item) => item.isForCurrentSpace);
	}

	@cached
	get goalForSpaceModelsWithoutObjectives(): Goal[] {
		return this.goalForSpaceModels.filter((item) => !item.isObjectiveType);
	}

	@cached
	get goalForClientModels(): Goal[] {
		return this.goalModels.filter((item) => !item.isForCurrentSpace);
	}

	@cached
	get goalForCompanyModels(): Goal[] {
		return this.goalForSpaceModelsWithoutObjectives.filter((item) => !item.groupId);
	}

	@cached
	get goalForTeamModels(): Goal[] {
		return this.goalForSpaceModelsWithoutObjectives.filter((item) => !!item.groupId);
	}

	@cached
	get myGoalsModels(): Goal[] {
		return this.goalForSpaceModelsWithoutObjectives.filter((item) => item.isOwnedByMe);
	}

	get isGoalsOKRsEnabled(): boolean {
		return this.billingService.goalsOKRsEnabled;
	}

	@cached
	get goalModelsAddedAsSubgoals(): Goal[] {
		const spaceGoals = this.goalForSpaceModels;
		const addedSubgoalIds = uniq(
			spaceGoals.flatMap((item) => item.subGoals.map((subgoal) => subgoal.goalId)),
		);
		return spaceGoals.filter(
			(item) => item.isGoalType && addedSubgoalIds.includes(item.numericId!),
		);
	}

	get companyStrategy(): string {
		return sanitize(this._companyStrategyValue);
	}

	private _hierarchyMap = new Map<Goal['id'], Goal>(); // childId: parent

	@cached
	get hierarchyMap(): Map<Goal['id'], Goal> {
		const newMap = new Map<Goal['id'], Goal>();
		this.goalModels.forEach((goal) => {
			goal.children.forEach((child) => {
				newMap.set(child.id, goal);
			});
		});
		// comparing the maps
		const oldMap = this._hierarchyMap;
		if (newMap.size === oldMap.size) {
			let allMatch = true;
			for (const [key, value] of newMap) {
				if (!oldMap.has(key) || oldMap.get(key) !== value) {
					allMatch = false;
					break;
				}
			}
			if (allMatch) {
				// no changes
				return this._hierarchyMap;
			}
		}

		// has changes
		this._hierarchyMap = newMap;
		return this._hierarchyMap;
	}

	generateTreeDataForGoals(goals: Goal[]): TreeItemData[] {
		// the goals given are the ones that match the filter
		// however, we need to show also all their children
		// the goals that match but their parent does not, become "top level" goals

		const getAllOffspringFromGoals = (goals: Goal[]): Goal[] => {
			return goals.flatMap((goal) => {
				const children = goal.children;
				if (children.length) {
					return [...children, ...getAllOffspringFromGoals(children)];
				} else {
					return [];
				}
			});
		};

		const validGoals = uniq([...goals, ...getAllOffspringFromGoals(goals)]);
		const orderedGoalData: { goal: Goal; depth: number }[] = [];
		const visited = new Set<Goal>();
		const hierarchyMap = this.hierarchyMap;

		const addGoalAndChildren = (goalData: { goal: Goal; depth: number }) => {
			if (visited.has(goalData.goal)) return;
			visited.add(goalData.goal);

			orderedGoalData.push(goalData);

			// Add children in order
			goalData.goal.children.forEach((child) => {
				if (validGoals.includes(child)) {
					addGoalAndChildren({ goal: child, depth: goalData.depth + 1 });
				}
			});
		};

		// Process each goal from the original argument and its descendants
		goals.forEach((goal) => {
			const parent = hierarchyMap.get(goal.id);
			if (!parent || !validGoals.includes(parent)) {
				addGoalAndChildren({ goal, depth: 0 });
			}
		});

		const validGoalIds = orderedGoalData.map((goalData) => goalData.goal.numericId!);

		const treeData: TreeItemData[] = orderedGoalData.map((goalData) => {
			const parent = hierarchyMap.get(goalData.goal.id);
			const parentId =
				parent && validGoalIds.includes(parent.numericId!)
					? parent.numericId!
					: TreeTopStrategyItemId;
			return {
				id: goalData.goal.numericId!,
				parentId,
				goal: goalData.goal,
				depth: goalData.depth,
			};
		});

		return treeData;
	}

	get hasAnySpaceGoals(): boolean {
		return this.goalForSpaceModels.length > 0;
	}

	getValidGoalsForMetricSetup(metricSetup: MetricSetup): Goal[] {
		// Valid goals either match or are more specific than the setup.
		// E.g. selection with metric that allows dimensions but without any dimension selected
		// will allow any goals for that metric and any of its dimensions.
		const {
			key,
			spaceAccessId,
			dimensions,
			attributeNamesFromBasicFilters,
			selectedUnit,
			hasUnits,
			allowBasicDimensionValueFilters,
		} = metricSetup;

		return this.goalForSpaceModels.filter((goal) => {
			const goalMetricSetup = goal.mainMetricSetup;
			if (
				goalMetricSetup.key !== key ||
				goalMetricSetup.spaceAccessId !== spaceAccessId ||
				!equal(goalMetricSetup.dimensions, dimensions)
			) {
				return false;
			}

			if (allowBasicDimensionValueFilters && attributeNamesFromBasicFilters.length) {
				if (
					!attributeNamesFromBasicFilters.includes(
						goalMetricSetup.firstAttributeNameFromBasicFilters,
					)
				) {
					return false;
				}
			}

			if (hasUnits && selectedUnit !== Unit.ALL) {
				if (goalMetricSetup.unitName !== selectedUnit) {
					return false;
				}
			}

			return true;
		});
	}

	getGoalForGoalKeyLong(goalKeyLong: string | null) {
		return this.goalForSpaceModels.find((item) => item.goalKeyLong === goalKeyLong);
	}

	openNewPopup(origin = '', onGoalSaved?: PopupData.Goal.Edit['onGoalSaved']) {
		const metricSetup = new MetricSetup();
		return this.openNewPopupForMetricSetup(metricSetup, origin, onGoalSaved);
	}

	openNewPopupForMetricSetup(
		metricSetup: MetricSetup,
		origin = '',
		onGoalSaved?: PopupData.Goal.Edit['onGoalSaved'],
		onlyAutomaticEnabled = false,
		isGoalTypeObjectiveDisabled = false,
	) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (this.billingService.checkIfCan(BillingFeature.GOALS_ADVANCED, true)) {
			const goal = this.createNewModel({
				type: GoalType.GOAL,
				progressType: GoalProgressType.AUTOMATIC,
				title: `${metricSetup.selectedMetricTitleWithUnit} goal`,
				mainMetricSetupKey: metricSetup.key!,
				mainMetricSetupSpaceAccessId: metricSetup.spaceAccessId,
				mainMetricSetupDimensions: metricSetup.dimensions,
				mainMetricSetupFilters: metricSetup.filtersData,
			});

			return this.createAndOpenCreatePopup({
				model: goal,
				origin,
				onGoalSaved,
				onlyAutomaticEnabled,
				isGoalTypeObjectiveDisabled,
			});
		}
		return null;
	}

	openCreatePopup(
		model: Goal,
		origin = '',
		onGoalSaved?: PopupData.Goal.Edit['onGoalSaved'],
		isGoalTypeObjectiveDisabled = false,
		isSubgoal = false,
	) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (this.billingService.checkIfCan(BillingFeature.GOALS, true)) {
			return this.createAndOpenCreatePopup({
				model,
				origin,
				onGoalSaved,
				isGoalTypeObjectiveDisabled,
				isSubgoal,
			});
		}
		return null;
	}

	openEditPopup(
		model: Goal,
		origin = '',
		onGoalSaved?: PopupData.Goal.Edit['onGoalSaved'],
		isNotificationsPopupOpen = false,
		onlyAutomaticEnabled = false,
		passOriginalModel = false,
		isGoalTypeObjectiveDisabled = false,
		isSubgoal = false,
	) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (this.billingService.checkIfCan(BillingFeature.GOALS, true)) {
			const modelToEdit = passOriginalModel ? model : this.createModelFromRecord(model.model);

			return this.createAndOpenEditPopup({
				model: modelToEdit,
				origin,
				onGoalSaved,
				isNotificationsPopupOpen,
				onlyAutomaticEnabled,
				isGoalTypeObjectiveDisabled,
				isSubgoal,
			});
		}
		return null;
	}

	openAdvancedSetup(
		// only growth and up
		model: Goal,
		metricSetup: MetricSetup,
		referencePoint: DChartPrimaryPoint | null,
		origin: string,
		changeReferencePoint: (referencePoint: DChartPrimaryPoint | null) => void,
	) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const popup = this.popupService.createModal(GoalPopupAdvancedSetupComponent, {
			popupClass: 'popup-type-goal-advanced-setup',
			allowStacked: true,
		});

		const modelCopy = this.createModelCopy(model);
		modelCopy.copyMainMetricLimitationsFrom(model);

		popup.open({
			model: modelCopy,
			metricSetup,
			referencePoint,
			origin,
			onSave: (advancedModel: Goal, newReferencePoint: DChartPrimaryPoint | null) => {
				model.endDate = advancedModel.endDate;
				model.targetValue = advancedModel.targetValue;
				model.primaryGranulation = advancedModel.primaryGranulation;
				model.targetValueType = advancedModel.targetValueType;
				model.historyValues = advancedModel.historyValues;
				model.targetSuccessCondition = advancedModel.targetSuccessCondition;
				model.measurement = advancedModel.measurement;
				model.historyValuesOverride = advancedModel.historyValuesOverride;
				model.calculationStrategyOverride = advancedModel.calculationStrategyOverride;

				changeReferencePoint(newReferencePoint);
			},
		});
	}

	openUpdateProgressPopup(model: Goal, logToEdit?: GoalLog) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const popup = this.popupService.createModal(GoalPopupUpdateProgressComponent, {
			popupClass: 'popup-type-goal-update-progress',
			allowStacked: true,
		});

		popup.open({
			model,
			logToEdit,
		});
	}

	toggleGoalFavorite(model: Goal, isNowFavorite = !model.favorite): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.currentUserService.handleDemo()) {
				resolve();
				return;
			}

			model.favorite = isNowFavorite;

			const layer = this.infolayerService.loading('Saving...');
			// update order to maintain favorites at top
			this.updateOrderAfterFavoriteToggle()
				.then(() => model.save())
				.then(
					() => {
						layer.ok(
							`Goal <strong>${model.title}</strong> ${isNowFavorite ? '' : 'un'}favorited${isNowFavorite ? ' and moved to the top' : ' and removed from the top'}!`,
						);
						resolve();
					},
					(errorData: RequestError) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(() => {
					this.requestService.showAllValidationErrors.bind(this.requestService);
					reject();
				});

			if (isNowFavorite) {
				const metrics = [model.mainMetric!];
				if (model.otherMetric) {
					metrics.push(model.otherMetric);
				}

				this.analyticsService.event(TrackingEvent.GOAL_FAVORITED, {
					datasources: uniq(metrics.map((item) => item.dataSourceTypeKey)),
					metrics: metrics.map((item) => item.name),
				});
			}
		});
	}

	deleteGoal(model: Goal, deleteCallback?: () => void, confirmCallback?: () => void): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			if (confirmCallback) {
				confirmCallback();
			}

			const layer = this.infolayerService.loading('Deleting goal...');
			model
				.delete()
				.then(
					() => {
						layer.ok('Goal deleted!');
						this.analyticsService.event(TrackingEvent.GOAL_DELETED);

						if (deleteCallback) {
							deleteCallback();
						}
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete the goal "${model.title}"?`,
			text: 'It will also delete any alerts set for this goal.',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	override batchFavorite(items: Goal[]) {
		return super.batchFavorite(items).then(() => {
			return this.updateOrderAfterFavoriteToggle();
		});
	}

	override batchUnfavorite(items: Goal[]) {
		return super.batchUnfavorite(items).then(() => {
			return this.updateOrderAfterFavoriteToggle();
		});
	}

	updateOrderAfterFavoriteToggle(): Promise<void> {
		const allGoals = sortBy(this.myGoalsModels, 'order', 'numericId');
		const favorites: Goal[] = [];
		const nonFavorites: Goal[] = [];

		allGoals.forEach((goal: Goal) => {
			if (goal.favorite) {
				favorites.push(goal);
			} else {
				nonFavorites.push(goal);
			}
		});

		const newOrderedGoals = [...favorites, ...nonFavorites];
		const newOrderIds = newOrderedGoals.map((goal) => goal.numericId!);

		newOrderedGoals.forEach((goal, index) => {
			goal.order = index;
			goal.applyBufferedChanges(['order']);
		});

		return this.requestService
			.api<RecordData.Payload>('POST', '/goals/order', newOrderIds)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	openGoalAccessTypePopup(model: Goal, saveOnChange = true, createModelCopy = false) {
		const goalModel = createModelCopy ? this.createModelCopy(model) : model;

		const onSave = (newAccess: number, selectedUserIds: number[]) => {
			if (newAccess !== UserAccess.PUBLIC) {
				if (model.children.length || !!model.parentGoal) {
					this.infolayerService.warning(
						'Goals that are part of the hierarchy must be public!',
					);
					return;
				}
			}

			const isAccessChangeSuccessful = model.changeAccess(newAccess, selectedUserIds);

			if (saveOnChange && isAccessChangeSuccessful) {
				void model.save();
			}
		};

		if (this.accountService.handleDemo()) {
			return;
		}

		const popup = this.popupService.createModal(CommonPopupAccessPermissionsComponent, {
			popupClass: 'popup-type-access-permissions',
			allowStacked: true,
		});
		popup.open({ model: goalModel, saveFn: onSave });
		return popup;
	}

	openGoalInsightsPage(goal: Goal): void {
		this.navigationService.transitionToRoute('goals.insights.detail', goal.model);
	}

	openGoalInsightsDrawer(goal: Goal) {
		const popup = this.popupService.createModal(GoalsInsightsDrawerPopupComponent, {
			popupClass: 'popup-type-drawer popup-type-goal-insights-drawer',
			showClose: false,
			hasOverlay: true,
			closeOnOverlayClick: false,
			allowStacked: true,
			isAutoCentered: false,
		});

		popup.open({
			model: goal,
			title: goal.title || 'Goal insights',
		});

		return popup;
	}

	openGoalInsightsDrawerByMetricKey(key: string): void {
		const goal = this.getGoalForGoalKeyLong(key);
		if (goal) {
			this.openGoalInsightsDrawer(goal);
		}
	}

	// Space goals load once per session; a goal Genie created since then is fetched on its own.
	async findOrRequestGoal(goalId: number): Promise<Goal | null> {
		const id = String(goalId);
		await this.requestSpaceGoals();

		const goal = this.findModel(id);
		if (goal) {
			return goal;
		}

		return (await this.requestAndPushQuietly(`/goals/${id}`)) ? this.findModel(id) : null;
	}

	async openGoalInsightsDrawerById(goalId: number): Promise<boolean> {
		const goal = await this.findOrRequestGoal(goalId);
		if (!goal) {
			return false;
		}

		// The drawer reads values from the goal's block; a goal fetched on its own arrives without it.
		const blockId = goal.primaryBlockId ? String(goal.primaryBlockId) : null;
		if (blockId && !this.mmBlocksService.findRecordQuick(blockId)) {
			if (!(await this.requestAndPushQuietly(`/blocks/${blockId}`))) {
				return false;
			}
			// With the block in the store, this requests only its data.
			void this.mmBlocksService.requestSingleBlock(blockId);
		} else {
			void this.requestSpaceGoalsData();
		}

		this.openGoalInsightsDrawer(goal);
		return true;
	}

	// The caller shows its own message, so the request layer's error snackbar is skipped.
	private async requestAndPushQuietly(url: string): Promise<boolean> {
		try {
			const reply = await this.requestService.api<RecordData.PushPayload>('GET', url, null, {
				skipShowingErrorInfolayer: true,
			});
			this.storeService.pushPayload(reply);
			return true;
		} catch (errorData) {
			// Logs a non-request throw; the request layer already logged a request error.
			this.requestService.handleGenericThrownError(errorData);
			return false;
		}
	}

	// Home's goal picker: the picked goal id, or null when the plan lacks Goals or the picker closes or can't open.
	async pickGoal(): Promise<number | null> {
		// Opens the upgrade popup itself.
		if (!this.billingService.checkIfCan(BillingFeature.GOALS, true)) {
			return null;
		}

		await this.requestSpaceGoals();

		return new Promise((resolve) => {
			const popup = this.popupService.createModal(HomePopupPickGoalComponent, {
				popupClass: 'popup-type-home-pick-goal',
				allowStacked: true, // "+ New goal" opens the goal create popup on top
			});
			// CLOSED follows every close, even one while still opening; after a submit it is a no-op.
			popup.on(PopupEvent.CLOSED, this, () => resolve(null));

			// A refused open (popups disallowed, the onboarding banner, a picker already open) fires no event.
			if (!popup.open({ onSubmit: resolve })) {
				resolve(null);
			}
		});
	}

	openGoalSelectorPopup(data: PopupData.Goal.Selector) {
		const popup = this.popupService.createModal(GoalPopupSelectorComponent, {
			popupClass: 'popup-type-goal-selector',
			allowStacked: true,
		});
		popup.open(data);
	}

	getGranulationItemString(
		isFixedTargetValue: boolean,
		timeUnit: TimeUnit,
		shorten = false,
	): string {
		const timeUnitText = DateRange.getTimeUnitText(timeUnit, isFixedTargetValue, true);

		const increaseText = shorten
			? `% ${timeUnitText[0]}o${timeUnitText[0]}`
			: `${timeUnitText} over ${timeUnitText}`;

		return isFixedTargetValue ? timeUnitText : increaseText;
	}

	private createAndOpenCreatePopup(props: PopupData.Goal.Create) {
		const popup = this.popupService.createModal(GoalPopupCreateComponent, {
			popupClass: 'popup-type-goal-create',
			allowStacked: true,
		});

		popup.open(props);

		return popup;
	}

	private createAndOpenEditPopup(props: PopupData.Goal.Edit) {
		const popup = this.popupService.createModal(GoalPopupEditComponent, {
			popupClass: 'popup-type-goal-edit',
			allowStacked: true, // goal advanced setup, changelog
		});

		popup.open(props);

		return popup;
	}

	getTargetEndDateTimestamp(endDate: string | null) {
		const targetEndDateTimeZone = this.currentUserService.model?.timezone || 'Etc/UTC';
		return endDate
			? moment.tz(endDate, TargetEndDateFormat, targetEndDateTimeZone).endOf('day').unix()
			: null;
	}

	getGoalOwnerScheduleTypeForPrimaryGranulation(
		primaryGranulation: GoalRangeTimeUnit,
	): GoalOwnerScheduleType {
		switch (primaryGranulation) {
			case TimeUnit.DAY:
			case TimeUnit.WEEK:
				return GoalOwnerScheduleType.WEEKLY;
			case TimeUnit.MONTH:
				return GoalOwnerScheduleType.BIWEEKLY;
			case TimeUnit.QUARTER:
			case TimeUnit.YEAR:
				return GoalOwnerScheduleType.MONTHLY;
			default:
				return GoalOwnerScheduleType.MONTHLY;
		}
	}

	getScheduleTypeForPrimaryGranulation(primaryGranulation: GoalRangeTimeUnit): ScheduleType {
		switch (primaryGranulation) {
			case TimeUnit.DAY:
			case TimeUnit.WEEK:
				return ScheduleType.DAILY;
			case TimeUnit.MONTH:
				return ScheduleType.WEEKLY;
			case TimeUnit.QUARTER:
			case TimeUnit.YEAR:
				return ScheduleType.MONTHLY;
			default:
				return ScheduleType.WEEKLY;
		}
	}

	isValueValid(value?: number | string | null): boolean {
		return (
			(typeof value === 'number' && Number.isFinite(value)) ||
			(typeof value === 'string' && value !== '' && Number.isFinite(Number(value)))
		);
	}

	@cached
	get savedGoalBlockIds(): Set<string> {
		const ids = new Set<string>();
		this.savedRecords.forEach((goal) => {
			if (goal.blockId) {
				ids.add(String(goal.blockId));
			}
		});
		return ids;
	}

	hasGoalForBlockId(blockId: string | number | null | undefined): boolean {
		if (!blockId) {
			return false;
		}
		return this.savedGoalBlockIds.has(String(blockId));
	}

	// ------ Requests ------

	@tracked isSingleGoalRequestLoading = false;
	requestSingleGoal(goalId: string) {
		this.isSingleGoalRequestLoading = true;
		return this.requestService
			.api<RecordData.Payload>('GET', `/goals/${goalId}`)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch((errorData: RequestError) => {
				this.requestService.handleGenericThrownError(errorData);
			})
			.finally(() => {
				this.isSingleGoalRequestLoading = false;
			});
	}

	requestSpaceGoals(preStoreFn?: () => Promise<void>): Promise<void> {
		return this.requestOnce(
			'spaceGoals',
			() => {
				// const testData = window.dbx_mock['GET /d/goals'];
				// return Promise.resolve(testData);

				return this.requestService.api<RecordData.Payload>(
					'GET',
					'/goals',
					{ type: 'space' },
					{ timeout: 60 * 1000 },
				);
			},
			preStoreFn,
		);
	}

	requestSpaceGoalsData(preStoreFn?: () => Promise<void>): Promise<void> {
		// data itself will be sent async over pusher after data request
		// so we need to trigger it after loading goals themselves to avoid metric data being garbage-collected
		const preRequestPromise = this.requestSpaceGoals().then(
			() => this.socketService.afterDataChannelSubscribed,
		);
		return preRequestPromise.then(() => {
			void this.requestOnce(
				'spaceGoalsData',
				() => {
					// const testData = window.dbx_mock['GET /d/goals/data'];
					// return Promise.resolve(testData);

					return this.requestService.api<RecordData.Payload>(
						'GET',
						'/goals/data',
						{ type: 'space' },
						{ timeout: 60 * 1000 },
					);
				},
				preStoreFn,
			);
		});
	}

	requestClientGoals(preStoreFn?: () => Promise<void>): Promise<void> {
		return this.requestOnce(
			'clientGoals',
			() => {
				return this.requestService.api<RecordData.Payload>('GET', '/goals', {
					type: 'client',
				});
			},
			preStoreFn,
		);
	}

	requestClientGoalsData(preStoreFn?: () => Promise<void>): Promise<void> {
		// data itself will be sent async over pusher after data request
		// so we need to trigger it after loading goals themselves to avoid metric data being garbage-collected
		const preRequestPromise = this.requestClientGoals().then(
			() => this.socketService.afterDataChannelSubscribed,
		);
		return preRequestPromise.then(() => {
			void this.requestOnce(
				'clientGoalsData',
				() => {
					return this.requestService.api<RecordData.Payload>('GET', '/goals/data', {
						type: 'client',
					});
				},
				preStoreFn,
			);
		});
	}

	requestOnce(
		variablePrefix: RequestVariablePrefix,
		requestPromiseFn: () => Promise<object | void>,
		preStoreFn?: () => Promise<void>,
	): Promise<void> {
		return this.requestService.dataRequestOnce<RequestVariablePrefix>(
			this,
			variablePrefix,
			requestPromiseFn,
			preStoreFn,
		);
	}

	requestStatusRevert(goalId: string) {
		return this.requestService
			.api<RecordData.Payload>('POST', `/goals/${goalId}/reset-status`)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch((errorData: RequestError) => {
				this.requestService.handleGenericThrownError(errorData);
			});
	}

	private _companyStrategyValue = '';
	requestCompanyStrategy() {
		return this.requestService
			.api<APIData.Goal.StrategyResponse>('GET', `/goals-strategy`)
			.then((reply) => {
				this._companyStrategyValue = reply.strategy || '';
			})
			.catch((errorData: RequestError) => {
				this.requestService.handleGenericThrownError(errorData);
			});
	}

	updateCompanyStrategy(newStrategy: string) {
		return this.requestService
			.api<APIData.Goal.StrategyResponse>('PUT', `/goals-strategy`, {
				strategy: newStrategy,
			})
			.then((reply) => {
				this._companyStrategyValue = reply.strategy || '';
			})
			.catch((errorData: RequestError) => {
				this.requestService.handleGenericThrownError(errorData);
			});
	}

	// -------- Reference value --------

	get isReferenceValueLoading() {
		return !!this._fetchReferenceValuePromise; // Only one reference value fetch operation can be in progress at a time
	}

	@tracked _fetchReferenceValuePromise: RSPromise<unknown> | null = null;
	fetchReferenceValue(
		metricSetup: MetricSetup,
		primaryGranulation: GoalRangeTimeUnit,
		startDateTs?: number | null,
		endDateTs?: number | null,
	): Promise<DChartPrimaryPoint | null> {
		const isTimeframeDateValid = !!startDateTs && !!endDateTs;

		let rangeConfig: DateRangeInterface.RangeInterface = {
			timeUnit: getGoalRangeTimeUnitForPrimaryGranulation(primaryGranulation),
			multiplier: 1,
			offset: -1,
			includeFuture: true,
		};

		if (isTimeframeDateValid && primaryGranulation === TimeUnit.TOTAL) {
			const startDateMoment = moment.unix(startDateTs);
			const endDateMoment = moment.unix(endDateTs + 1);
			const daysDiff = endDateMoment.diff(startDateMoment, 'days');

			rangeConfig = {
				startDate: startDateMoment
					.subtract(daysDiff, 'days')
					.format(TargetEndDateFormat) as DateRangeInterface.IdDate,
				endDate: endDateMoment
					.subtract(daysDiff, 'days')
					.format(TargetEndDateFormat) as DateRangeInterface.IdDate,
			};
		}

		const dataSelect = this.mmDataSelectsService.createNewModel({
			dateRange: {
				range: rangeConfig,
				granulation: {
					isWholeRange: true,
				},
			},
			metricSetupKey: metricSetup.key,
			metricSetupSpaceAccessId: metricSetup.spaceAccessId,
			metricSetupDimensions: metricSetup.dimensions,
			metricSetupFilters: metricSetup.filtersData,
		});

		const previousPromise = this._fetchReferenceValuePromise;
		if (previousPromise) {
			(previousPromise.jqPromise as RSjqXHR | undefined)?.abort();
		}

		const promise = this.mmDataSelectsService
			.fetchData(dataSelect)
			.then((reply: DSData.RootTop) => {
				dataSelect.setFakeData(reply);

				const output = dataSelect.outputs[0];
				if (output) {
					const referencePoint = this.mmSamplesService.createDChartPointFromPoint(
						output,
						output.point,
					);

					return referencePoint;
				}

				return null;
			})
			.catch(() => {
				return null;
			})
			.finally(() => {
				// cleanup promise and model
				if (promise === this._fetchReferenceValuePromise) {
					this._fetchReferenceValuePromise = null;
				}
				void dataSelect.delete(true);
			});

		this._fetchReferenceValuePromise = promise;

		return promise;
	}

	// -------- Form layout --------

	@tracked formLayoutCache: APIData.Goal.FormLayout.Response = {};
	@tracked private requestFormLayoutPromise: Promise<unknown> | null = null;

	get isFormLayoutCacheEmpty() {
		return !Object.keys(this.formLayoutCache).length;
	}

	// request form layout(periods) and create new rows or missing rows
	requestFormLayout(dateRange: DateRange): Promise<void> {
		// request form layout for given date range
		// chain the requests, if necessary
		const preRequestPromise = this.requestFormLayoutPromise || Promise.resolve();

		const goalPeriodsRequest = this.requestGoalPeriodsForDateRange(dateRange) // request periods
			.then((reply) => {
				this.updateFormLayoutCache(reply); // set form layout cache and create new rows or missing rows
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

		const promise = preRequestPromise
			.then(() => goalPeriodsRequest)
			.finally(() => {
				if (this.requestFormLayoutPromise === promise) {
					this.requestFormLayoutPromise = null;
				}
			});

		this.requestFormLayoutPromise = promise;

		return promise;
	}

	async requestFullFormLayout(model: Goal): Promise<void> {
		this.formLayoutCache = {};

		const dateRange = await this.resolveFormLayoutDateRange(model);
		return this.requestFormLayout(dateRange);
	}

	private async resolveFormLayoutDateRange(model: Goal): Promise<DateRange> {
		const dateFormat = TargetEndDateFormat;
		const tz = this.currentUserService.model?.timezone || 'Etc/UTC';

		const historyStartDate = model.startDateFromHistoryValues;
		const historyEndDate = model.endDateFromHistoryValues;

		// Use the goal's timeframe, or derive a full-year timeframe from history values
		let yearForPeriods = model.timeframe;
		if (!yearForPeriods && historyStartDate) {
			const historyYear = moment.tz(historyStartDate, dateFormat, tz).year();
			yearForPeriods = { year: historyYear, type: TimeframeType.Y };
		}

		if (!yearForPeriods) {
			return this.createRelativeFallbackDateRange();
		}

		// Fetch full year boundaries from the timeframe-periods endpoint
		// This returns calendar-aware dates (respects fiscal calendar if configured)
		const periodsResponse = await this.timeframeService.getTimeframePeriods(yearForPeriods);
		const yearPeriod = periodsResponse
			? this.timeframeService.findPeriod(periodsResponse, TimeframeType.Y)
			: null;

		if (!yearPeriod) {
			return this.createRelativeFallbackDateRange();
		}

		const yearStart = moment.unix(yearPeriod.periodStartTS).tz(tz).format(dateFormat);
		const yearEnd = moment
			.unix(yearPeriod.periodEndTS - 1)
			.tz(tz)
			.format(dateFormat);

		// Extend range to cover history values and custom end date
		const rangeStart = this.earliestDate(yearStart, historyStartDate);
		const rangeEnd = this.latestDate(yearEnd, historyEndDate, model.endDate);

		return DateRange.createFromFormula({
			range: {
				includeFuture: true,
				startDate: rangeStart as DateRangeInterface.IdDate,
				endDate: rangeEnd as DateRangeInterface.IdDate,
			},
		});
	}

	private earliestDate(date: string, ...others: (string | null)[]): string {
		return others.reduce<string>((earliest, candidate) => {
			return candidate && candidate < earliest ? candidate : earliest;
		}, date);
	}

	private latestDate(date: string, ...others: (string | null)[]): string {
		return others.reduce<string>((latest, candidate) => {
			return candidate && candidate > latest ? candidate : latest;
		}, date);
	}

	private createRelativeFallbackDateRange(): DateRange {
		return DateRange.createFromFormula({
			range: {
				includeFuture: true,
				timeUnit: TimeUnit.YEAR,
				multiplier: 3,
				offset: 1,
			},
		});
	}

	updateFormLayout(
		valueRows: GoalValueRow[],
		valueRowsByTimeUnit: ValueRowsByTimeUnit,
		isMetricPercentage = false,
		isMetricInverted = false,
	) {
		const isFirstLoad = this.isFormLayoutCacheEmpty;

		let newValueRows: GoalValueRow[];

		if (isFirstLoad) {
			newValueRows = createRowsFromLayoutCache(
				this.formLayoutCache,
				isMetricPercentage,
				isMetricInverted,
			);
		} else {
			newValueRows = [
				...valueRows,
				...createMissingRowsFromLayoutCache(
					valueRowsByTimeUnit,
					this.formLayoutCache,
					isMetricPercentage,
					isMetricInverted,
				),
			];
		}

		return newValueRows;
	}

	checkToRebuildFullForm(valueRows: GoalValueRow[], model: Goal): Promise<void> {
		// check if is necessary to rebuild full form layout
		const endDate = model.endDate ?? model.endDateFromHistoryValues;

		let needToRequest = false;
		if (valueRows.length) {
			// if we already have layout, just check if endDate is included (w/o yearly)
			const targetEndDateTs = this.getTargetEndDateTimestamp(endDate);

			if (targetEndDateTs) {
				const rowsWithoutYearly = valueRows.filter((r) => r.timeUnit !== TimeUnit.YEAR);
				needToRequest = !findRowWithTimestamp(rowsWithoutYearly, targetEndDateTs);
			}
		} else {
			needToRequest = true;
		}

		if (needToRequest) {
			return this.requestFullFormLayout(model);
		} else {
			return Promise.resolve();
		}
	}

	markRowHiddenAndClearValue(valueRows: GoalValueRow[], valuesLastDateTs: number | null) {
		valueRows.forEach((r) => {
			const isHidden = !!valuesLastDateTs && valuesLastDateTs < r.periodStartTs;
			r.isHidden = isHidden;
			if (isHidden) {
				r.updateValue(null);
			}
		});
	}

	private updateFormLayoutCache(reply: APIData.Goal.FormLayout.Response) {
		// updates form layout cache with new data (reply)
		const cache = this.formLayoutCache;

		Object.keys(reply).forEach((timeUnit) => {
			if (cache[timeUnit]) {
				reply[timeUnit]!.forEach((page) => {
					if (!cache[timeUnit]!.find((p) => p.pageId === page.pageId)) {
						cache[timeUnit]!.push(page);
					}
				});
			} else {
				// first load
				cache[timeUnit] = reply[timeUnit]!;
			}
		});
		// trigger change

		this.formLayoutCache = cache;
	}

	private requestGoalPeriodsForDateRange(dateRange: DateRange) {
		const payload = {
			dateRange: {
				range: dateRange?.rangeFormula,
			},
		};

		return this.requestService.api<APIData.Goal.FormLayout.Response>(
			'POST',
			'/goals/periods',
			payload,
			{
				timeout: 2 * 60 * 1000,
			},
		);
	}

	// ----- Sub-goals ----------

	getDescendantIds(parentId: number, orderedGoalData: TreeItemData[]): number[] {
		const descendants: number[] = [];
		const getDescendants = (pid: number) => {
			for (const item of orderedGoalData) {
				if (item.parentId === pid) {
					descendants.push(item.id);
					getDescendants(item.id);
				}
			}
		};
		getDescendants(parentId);
		return descendants;
	}

	// ----- Statuses & Other -----

	getStatusText(status?: GoalStatus | null) {
		switch (status) {
			case GoalStatus.ON_TRACK:
				return 'On-track';
			case GoalStatus.AT_RISK:
				return 'At-risk';
			case GoalStatus.OFF_TRACK:
				return 'Off-track';
			case GoalStatus.ACHIEVED:
				return 'Achieved';
			case GoalStatus.PARTIAL:
				return 'Partial';
			case GoalStatus.MISSED:
				return 'Missed';
			default:
				return 'No status';
		}
	}

	getStatusClass(status?: GoalStatus | null) {
		switch (status) {
			case GoalStatus.ON_TRACK:
			case GoalStatus.ACHIEVED:
				return 'done';
			case GoalStatus.AT_RISK:
			case GoalStatus.PARTIAL:
				return 'medium';
			case GoalStatus.OFF_TRACK:
			case GoalStatus.MISSED:
				return 'low';
			default:
				return 'none';
		}
	}

	getStatusColor(status?: GoalStatus | null) {
		switch (status) {
			case GoalStatus.ON_TRACK:
			case GoalStatus.ACHIEVED:
				return '#25a767'; // --databox-green-500
			case GoalStatus.AT_RISK:
			case GoalStatus.PARTIAL:
				return '#fa870c'; // --databox-orange-500
			case GoalStatus.OFF_TRACK:
			case GoalStatus.MISSED:
				return '#e62828'; // --databox-red-500
			default:
				return '#8e929b'; // --databox-neutral-400
		}
	}

	getIconName(status?: GoalStatus | null) {
		switch (status) {
			case GoalStatus.ACHIEVED:
				return 'Check';
			case GoalStatus.PARTIAL:
				return 'Minus';
			case GoalStatus.MISSED:
				return 'Close';
			default:
				return 'CircleFill';
		}
	}

	getIsGoalComplete(status?: GoalStatus | null) {
		return (
			!!status &&
			[GoalStatus.ACHIEVED, GoalStatus.PARTIAL, GoalStatus.MISSED].includes(status)
		);
	}

	getDefaultTargetValue(isValuePercentual: boolean): number {
		return isValuePercentual ? 30 : 3000;
	}

	getScheduleTypeArrayOfString(model: Goal) {
		const scheduleType = model.isManualGoal ? model.ownerScheduleType : model.scheduleTypes;

		if (!scheduleType) {
			return ['not set'];
		}

		if (Array.isArray(scheduleType)) {
			return scheduleType.map((type) => this.getScheduleTypeString(type));
		}

		return [this.getOwnerScheduleTypeString(scheduleType)];
	}

	getTargetSuccessConditionString(condition: GoalTargetSuccessCondition | null) {
		switch (condition) {
			case GoalTargetSuccessCondition.REACH:
				return 'reach';
			case GoalTargetSuccessCondition.ABOVE:
				return 'above';
			case GoalTargetSuccessCondition.BELOW:
				return 'below';
			case GoalTargetSuccessCondition.METRIC:
				return 'metric';
			default:
				return 'not set';
		}
	}

	@cached
	get measurementOptions() {
		const options = [
			{
				label: 'Percent (%)',
				label2: '%',
				value: String(GoalFormatType.PERCENTAGE),
				isSeparated: false,
			},
			{
				label: 'Number (#)',
				label2: '#',
				value: String(GoalFormatType.NUMBER),
				isSeparated: false,
			},
		];

		this.constantsService.goalFormatUnits.forEach((formatUnit, idx) => {
			options.push({
				label: `${formatUnit.label} (${formatUnit.value})`,
				label2: formatUnit.value,
				value: formatUnit.value,
				isSeparated: !idx,
			});
		});

		return options;
	}

	getTextForSuccessCondition(condition: GoalTargetSuccessCondition, changeInvert = false) {
		const getTextForAboveBelow = (isBelow = false) => {
			return `I want to stay ${isBelow ? 'below' : 'above'}`;
		};
		switch (condition) {
			case GoalTargetSuccessCondition.REACH:
				return 'I want to reach';
			case GoalTargetSuccessCondition.METRIC:
				return getTextForAboveBelow(changeInvert);
			case GoalTargetSuccessCondition.ABOVE:
				return getTextForAboveBelow(false);
			case GoalTargetSuccessCondition.BELOW:
				return getTextForAboveBelow(true);
		}
	}

	// ------ For fake blocks (preview) ------

	generateGoalInsightsUUID() {
		return -1 * Number(`${FakeModelIdPrefix.GOAL_INSIGHTS}${uniqueId()}`);
	}

	isTemporaryFakeId(id: string | null = '') {
		const fakeModelPrefix = FakeModelIdPrefix.GOAL_INSIGHTS;
		const length = fakeModelPrefix.length;
		return !!id && id.substring(1, 1 + length) === fakeModelPrefix;
	}

	private getOwnerScheduleTypeString(scheduleType: GoalOwnerScheduleType) {
		switch (scheduleType) {
			case GoalOwnerScheduleType.QUARTERLY:
				return 'quarterly';
			case GoalOwnerScheduleType.MONTHLY:
				return 'monthly';
			case GoalOwnerScheduleType.BIWEEKLY:
				return 'biweekly';
			case GoalOwnerScheduleType.WEEKLY:
				return 'weekly';
			default:
				return 'not set';
		}
	}

	private getScheduleTypeString(scheduleType: ScheduleType) {
		switch (scheduleType) {
			case ScheduleType.ONE_OFF:
				return 'one-off';
			// case ScheduleType.YEARLY:
			// 	return 'yearly';
			case ScheduleType.QUARTERLY:
				return 'quarterly';
			case ScheduleType.MONTHLY:
				return 'monthly';
			case ScheduleType.WEEKLY:
				return 'weekly';
			case ScheduleType.DAILY:
				return 'daily';
			default:
				return 'not set';
		}
	}

	// ------ For-store ------

	modelName = ModelName.GOAL;

	createModel(model: GoalRecord): Goal {
		return new Goal({ model });
	}

	override createNewModel(properties: CreateRecordProperties<GoalRecord> = {}): Goal {
		if (typeof properties.ownerId === 'undefined') {
			properties.ownerId = this.currentUserService.numericId!;
		}
		if (typeof properties.users === 'undefined') {
			const currentUserId = this.currentUserService.numericId!;
			properties.users = [currentUserId];
		}
		if (typeof properties.timeframe === 'undefined') {
			properties.timeframe = {
				type: TimeframeType.Y,
				year: new Date().getFullYear(),
			};
		}
		return super.createNewModel(properties);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/goals': ModelManagerGoalsService;
	}
}
